import { useCallback, useRef, useState } from "react";

/**
 * Lets an action run once at a time, however it is triggered.
 *
 *   const [saving, guard] = useSubmitGuard();
 *   const save = guard(async () => { await axios.put(...); });
 *   <button onClick={save} disabled={saving} aria-busy={saving}>Save</button>
 *
 * Nothing used to stop a form being sent again while its request was still out.
 * A double-click, or Enter followed by a click, sent the request twice: two
 * identical toasts, and for bookings, refills and sign-ups, two real requests.
 *
 * The lock is a ref, not state. Two clicks can land before React re-renders, and
 * both would read the same `busy === false` from their closure; a ref is seen by
 * the second click immediately. `busy` is state only so the button can show it.
 *
 * The wrapped function must return or await its request. A `.then()` chain that
 * nobody returns finishes instantly as far as the guard can tell, and the lock
 * would lift before the request comes back.
 *
 * One guard per component covers every action in it: a second action while one
 * is in flight is ignored too, which is what those screens want.
 */
export function useSubmitGuard() {
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);

  const guard = useCallback(
    (action) =>
      async (...args) => {
        if (inFlight.current) return undefined;
        inFlight.current = true;
        setBusy(true);
        try {
          return await action(...args);
        } finally {
          inFlight.current = false;
          setBusy(false);
        }
      },
    []
  );

  return [busy, guard];
}
